import { useRef, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type CardSide = 'front' | 'back';

const CARD_VARIANTS = [
  { id: 1, label: 'Turuncu & Koyu' },
  { id: 2, label: 'Beyaz & Minimal' },
  { id: 3, label: 'Koyu Lüks' },
];

export default function BusinessCardPage() {
  const [activeSide, setActiveSide] = useState<CardSide>('front');
  const [activeVariant, setActiveVariant] = useState(1);
  const [flipped, setFlipped] = useState(false);
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);

  const handleFlip = () => {
    setFlipped((f) => !f);
    setActiveSide((s) => (s === 'front' ? 'back' : 'front'));
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] font-['Inter',sans-serif]">
      <Navbar />

      {/* Print styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,700&display=swap');
        @media print {
          body * { visibility: hidden !important; }
          #print-area, #print-area * { visibility: visible !important; }
          #print-area { position: fixed; top: 0; left: 0; width: 100vw; display: flex; gap: 32px; justify-content: center; align-items: center; padding: 40px; background: white; }
        }
        .card-3d { perspective: 1200px; }
        .card-inner { transition: transform 0.7s cubic-bezier(.4,2,.6,1); transform-style: preserve-3d; }
        .card-inner.flipped { transform: rotateY(180deg); }
        .card-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
        .card-back-face { transform: rotateY(180deg); }
      `}</style>

      <main className="pt-32 pb-24">
        <div className="max-w-6xl mx-auto px-6">

          {/* Başlık */}
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#FF6B35] uppercase mb-3">Dijital Kartvizit</span>
            <h1 className="text-4xl font-bold text-[#1A2332] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bioetiket Kartvizit
            </h1>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Profesyonel etiket baskı hizmetlerimizi tanıtan kartvizit tasarımlarımızı inceleyin ve yazdırın.
            </p>
          </div>

          {/* Varyant Seçici */}
          <div className="flex justify-center gap-3 mb-12">
            {CARD_VARIANTS.map((v) => (
              <button
                key={v.id}
                onClick={() => setActiveVariant(v.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer border ${
                  activeVariant === v.id
                    ? 'bg-[#FF6B35] text-white border-[#FF6B35] shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF6B35] hover:text-[#FF6B35]'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* Kart Önizleme */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-16">

            {/* 3D Kart */}
            <div className="flex flex-col items-center gap-6">
              <div className="card-3d w-[420px] h-[240px]">
                <div className={`card-inner w-full h-full relative ${flipped ? 'flipped' : ''}`}>

                  {/* ÖN YÜZ */}
                  <div className="card-face absolute inset-0 rounded-2xl overflow-hidden shadow-2xl">
                    {activeVariant === 1 && <CardFrontOrange />}
                    {activeVariant === 2 && <CardFrontWhite />}
                    {activeVariant === 3 && <CardFrontDark />}
                  </div>

                  {/* ARKA YÜZ */}
                  <div className="card-face card-back-face absolute inset-0 rounded-2xl overflow-hidden shadow-2xl">
                    {activeVariant === 1 && <CardBackOrange />}
                    {activeVariant === 2 && <CardBackWhite />}
                    {activeVariant === 3 && <CardBackDark />}
                  </div>

                </div>
              </div>

              {/* Çevir Butonu */}
              <button
                onClick={handleFlip}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-all shadow-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                {flipped ? 'Ön Yüzü Göster' : 'Arka Yüzü Göster'}
              </button>

              <p className="text-xs text-gray-400">
                Şu an görüntüleniyor: <span className="font-semibold text-[#FF6B35]">{flipped ? 'Arka Yüz' : 'Ön Yüz'}</span>
              </p>
            </div>

            {/* Bilgi Paneli */}
            <div className="max-w-sm w-full">
              <div className="bg-white rounded-2xl shadow-md p-8 border border-gray-100">
                <h3 className="text-lg font-semibold text-[#1A2332] mb-6">Kartvizit Bilgileri</h3>

                <div className="space-y-4 text-sm">
                  <InfoRow icon="ri-building-line" label="Firma" value="Bioetiket" />
                  <InfoRow icon="ri-map-pin-line" label="Adres" value="Yukarı Dudullu Mah. Necip Fazıl Blv. No:70, Ümraniye / İstanbul" />
                  <InfoRow icon="ri-phone-line" label="Sabit" value="+90 216 365 88 78" />
                  <InfoRow icon="ri-smartphone-line" label="Mobil" value="+90 532 601 82 94" />
                  <InfoRow icon="ri-phone-fill" label="Satış" value="+90 537 020 77 88" />
                  <InfoRow icon="ri-mail-line" label="E-posta" value="info@bioetiket.com" />
                  <InfoRow icon="ri-global-line" label="Web" value="www.bioetiket.com" />
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
                  <button
                    onClick={handlePrint}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-[#FF6B35] text-white rounded-xl text-sm font-semibold hover:bg-[#E55A2B] transition-all shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-printer-line text-base"></i>
                    Yazdır / PDF Kaydet
                  </button>
                  <p className="text-xs text-gray-400 text-center">
                    Standart kartvizit boyutu: 85 × 55 mm
                  </p>
                </div>
              </div>

              {/* Tasarım Notu */}
              <div className="mt-4 bg-[#FFF5F0] rounded-xl p-4 border border-[#FFD5C2]">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center text-[#FF6B35]">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </div>
                  <p className="text-xs text-[#8B4513] leading-relaxed">
                    Kartvizitinizi yazdırmak için tarayıcının yazdır menüsünden <strong>"PDF olarak kaydet"</strong> seçeneğini kullanabilirsiniz. En iyi sonuç için 300 DPI baskı önerilir.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tüm Varyantlar - Print Area */}
          <div id="print-area" className="hidden print:flex gap-8 justify-center mt-16">
            <div className="w-[340px] h-[194px] rounded-xl overflow-hidden shadow-lg">
              {activeVariant === 1 && <CardFrontOrange />}
              {activeVariant === 2 && <CardFrontWhite />}
              {activeVariant === 3 && <CardFrontDark />}
            </div>
            <div className="w-[340px] h-[194px] rounded-xl overflow-hidden shadow-lg">
              {activeVariant === 1 && <CardBackOrange />}
              {activeVariant === 2 && <CardBackWhite />}
              {activeVariant === 3 && <CardBackDark />}
            </div>
          </div>

          {/* Tüm Tasarımlar Önizleme */}
          <div className="mt-24">
            <h2 className="text-2xl font-bold text-[#1A2332] text-center mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              Tüm Tasarımlar
            </h2>
            <div className="grid md:grid-cols-3 gap-10">
              {CARD_VARIANTS.map((v) => (
                <div
                  key={v.id}
                  onClick={() => { setActiveVariant(v.id); setFlipped(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`cursor-pointer group rounded-2xl overflow-hidden transition-all duration-300 ${
                    activeVariant === v.id ? 'ring-2 ring-[#FF6B35] shadow-xl' : 'shadow-md hover:shadow-xl'
                  }`}
                >
                  <div className="w-full aspect-[85/55] overflow-hidden">
                    {v.id === 1 && <CardFrontOrange />}
                    {v.id === 2 && <CardFrontWhite />}
                    {v.id === 3 && <CardFrontDark />}
                  </div>
                  <div className="bg-white px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{v.label}</span>
                    {activeVariant === v.id && (
                      <span className="text-xs text-[#FF6B35] font-semibold flex items-center gap-1">
                        <i className="ri-check-line"></i> Seçili
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 flex items-center justify-center text-[#FF6B35] flex-shrink-0 mt-0.5">
        <i className={`${icon} text-base`}></i>
      </div>
      <div>
        <span className="text-gray-400 text-xs block">{label}</span>
        <span className="text-gray-800 font-medium">{value}</span>
      </div>
    </div>
  );
}

/* ─── VARYANT 1: Turuncu & Koyu ─── */
function CardFrontOrange() {
  return (
    <div className="w-full h-full bg-[#1A2332] relative overflow-hidden flex flex-col justify-between p-7">
      {/* Dekoratif daireler */}
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FF6B35]/20"></div>
      <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#FF6B35]/10"></div>
      <div className="absolute top-1/2 right-8 w-1 h-16 bg-[#FF6B35]/40 rounded-full -translate-y-1/2"></div>

      {/* Logo & Slogan */}
      <div className="relative z-10">
        <img
          src="https://static.readdy.ai/image/09a8ead7418a68887e61601399480989/f761e44d359e887ba9b77959d97e14a2.png"
          alt="Bioetiket Logo"
          className="h-9 w-auto object-contain brightness-0 invert"
        />
        <p className="text-[#FF6B35] text-xs mt-2 tracking-widest uppercase font-medium">
          Profesyonel Etiket Baskı
        </p>
      </div>

      {/* İsim & Unvan */}
      <div className="relative z-10">
        <p className="text-white text-lg font-semibold leading-tight">Satış & Müşteri Hizmetleri</p>
        <p className="text-gray-400 text-xs mt-1">Etiket Çözümleri Uzmanı</p>
      </div>
    </div>
  );
}

function CardBackOrange() {
  return (
    <div className="w-full h-full bg-[#FF6B35] relative overflow-hidden flex flex-col justify-between p-7">
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10"></div>
      <div className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full bg-white/10"></div>

      <div className="relative z-10">
        <p className="text-white/80 text-xs tracking-widest uppercase font-medium mb-4">İletişim</p>
        <div className="space-y-2">
          <ContactLine icon="ri-phone-line" text="+90 216 365 88 78  |  +90 532 601 82 94" />
          <ContactLine icon="ri-mail-line" text="info@bioetiket.com" />
          <ContactLine icon="ri-global-line" text="www.bioetiket.com" />
          <ContactLine icon="ri-map-pin-line" text="Yukarı Dudullu, Ümraniye / İstanbul" />
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-between">
        <p className="text-white/60 text-xs">Gıda · Kozmetik · İlaç · Endüstriyel</p>
        <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
          <i className="ri-award-line text-white text-sm"></i>
        </div>
      </div>
    </div>
  );
}

/* ─── VARYANT 2: Beyaz & Minimal ─── */
function CardFrontWhite() {
  return (
    <div className="w-full h-full bg-white relative overflow-hidden flex flex-col justify-between p-7 border border-gray-100">
      {/* Turuncu şerit */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#FF6B35]"></div>
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#FF6B35]"></div>

      <div className="pl-3">
        <img
          src="https://static.readdy.ai/image/09a8ead7418a68887e61601399480989/f761e44d359e887ba9b77959d97e14a2.png"
          alt="Bioetiket Logo"
          className="h-9 w-auto object-contain"
        />
        <p className="text-[#FF6B35] text-xs mt-1.5 tracking-widest uppercase font-medium">
          Profesyonel Etiket Baskı
        </p>
      </div>

      <div className="pl-3">
        <p className="text-[#1A2332] text-base font-semibold">Satış & Müşteri Hizmetleri</p>
        <p className="text-gray-400 text-xs mt-0.5">Etiket Çözümleri Uzmanı</p>
        <div className="mt-3 flex gap-2">
          <span className="text-xs bg-[#FFF0E8] text-[#FF6B35] px-2 py-0.5 rounded-full font-medium">Gıda</span>
          <span className="text-xs bg-[#FFF0E8] text-[#FF6B35] px-2 py-0.5 rounded-full font-medium">Kozmetik</span>
          <span className="text-xs bg-[#FFF0E8] text-[#FF6B35] px-2 py-0.5 rounded-full font-medium">İlaç</span>
        </div>
      </div>
    </div>
  );
}

function CardBackWhite() {
  return (
    <div className="w-full h-full bg-white relative overflow-hidden flex flex-col justify-between p-7 border border-gray-100">
      <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-[#FF6B35]"></div>
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF6B35]"></div>

      <div>
        <p className="text-[#FF6B35] text-xs tracking-widest uppercase font-semibold mb-4">İletişim Bilgileri</p>
        <div className="space-y-2">
          <ContactLineGray icon="ri-phone-line" text="+90 216 365 88 78  |  +90 532 601 82 94" />
          <ContactLineGray icon="ri-mail-line" text="info@bioetiket.com" />
          <ContactLineGray icon="ri-global-line" text="www.bioetiket.com" />
          <ContactLineGray icon="ri-map-pin-line" text="Yukarı Dudullu, Ümraniye / İstanbul" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-gray-300 text-xs">© 2025 Bioetiket</p>
        <div className="w-8 h-8 flex items-center justify-center bg-[#FFF0E8] rounded-full">
          <i className="ri-award-line text-[#FF6B35] text-sm"></i>
        </div>
      </div>
    </div>
  );
}

/* ─── VARYANT 3: Koyu Lüks ─── */
function CardFrontDark() {
  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col justify-between p-7"
      style={{ background: 'linear-gradient(135deg, #0F1419 0%, #1A2332 60%, #2A3547 100%)' }}>
      {/* Altın aksan */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent"></div>
      <div className="absolute -right-16 top-1/2 -translate-y-1/2 w-48 h-48 rounded-full border border-[#D4A853]/20"></div>
      <div className="absolute -right-8 top-1/2 -translate-y-1/2 w-28 h-28 rounded-full border border-[#D4A853]/15"></div>

      <div className="relative z-10">
        <img
          src="https://static.readdy.ai/image/09a8ead7418a68887e61601399480989/f761e44d359e887ba9b77959d97e14a2.png"
          alt="Bioetiket Logo"
          className="h-9 w-auto object-contain brightness-0 invert"
        />
        <p className="text-[#D4A853] text-xs mt-2 tracking-widest uppercase font-medium">
          Profesyonel Etiket Baskı
        </p>
      </div>

      <div className="relative z-10">
        <p className="text-white text-base font-semibold">Satış & Müşteri Hizmetleri</p>
        <p className="text-[#D4A853]/70 text-xs mt-1">Etiket Çözümleri Uzmanı</p>
      </div>
    </div>
  );
}

function CardBackDark() {
  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col justify-between p-7"
      style={{ background: 'linear-gradient(135deg, #0F1419 0%, #1A2332 60%, #2A3547 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent"></div>

      <div className="relative z-10">
        <p className="text-[#D4A853] text-xs tracking-widest uppercase font-medium mb-4">İletişim</p>
        <div className="space-y-2">
          <ContactLineGold icon="ri-phone-line" text="+90 216 365 88 78  |  +90 532 601 82 94" />
          <ContactLineGold icon="ri-mail-line" text="info@bioetiket.com" />
          <ContactLineGold icon="ri-global-line" text="www.bioetiket.com" />
          <ContactLineGold icon="ri-map-pin-line" text="Yukarı Dudullu, Ümraniye / İstanbul" />
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-between">
        <p className="text-gray-600 text-xs">Gıda · Kozmetik · İlaç · Endüstriyel</p>
        <div className="w-8 h-8 flex items-center justify-center border border-[#D4A853]/40 rounded-full">
          <i className="ri-award-line text-[#D4A853] text-sm"></i>
        </div>
      </div>
    </div>
  );
}

/* ─── Yardımcı Bileşenler ─── */
function ContactLine({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-white/80 text-xs`}></i>
      </div>
      <span className="text-white text-xs leading-tight">{text}</span>
    </div>
  );
}

function ContactLineGray({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-[#FF6B35] text-xs`}></i>
      </div>
      <span className="text-gray-600 text-xs leading-tight">{text}</span>
    </div>
  );
}

function ContactLineGold({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-[#D4A853] text-xs`}></i>
      </div>
      <span className="text-gray-400 text-xs leading-tight">{text}</span>
    </div>
  );
}
